import { useMemo, useState } from "react";
import { Download } from "lucide-react";

import { timetableService, type TimetablePeriod, type TimetableSlot } from "../../services/timetableService";
import { ClientRegister, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import type { RegisterConfig } from "../register/registerLogic";
import { byNumber } from "../register/registerLogic";
import { dayName, lessonConfig, periodTime, teacherLoads } from "./timetableLogic";
import { WeekGrid } from "./WeekGrid";
import { PeriodGrid } from "./PeriodGrid";
import { LayoutSwitch, useWeekLayout } from "./LayoutSwitch";
import { teacherName, type Option, type Teacher } from "./SetupPanels";

type Load = ReturnType<typeof teacherLoads>[number];

const loadConfig: RegisterConfig<Load> = {
  search: (row) => [row.name, ...row.classes].join(" "),
  views: [{ key: "all", label: "All" }],
  sorts: [
    { key: "-lessons", label: "Most lessons", compare: (a, b) => b.lessons - a.lessons },
    { key: "lessons", label: "Fewest lessons", compare: byNumber<Load>((row) => row.lessons) },
    { key: "name", label: "Name (A–Z)", compare: (a, b) => a.name.localeCompare(b.name) },
  ],
};
const loadColumns: RegisterColumn<Load>[] = [
  { header: "Teacher", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
  { header: "Lessons a week", cell: (row) => row.lessons, exportValue: (row) => row.lessons, align: "right" },
  { header: "Classes", cell: (row) => row.classes.size, exportValue: (row) => row.classes.size, align: "right" },
  { header: "Taught", cell: (row) => [...row.classes].sort().join(", "), exportValue: (row) => [...row.classes].sort().join(", "), className: "min-w-[14rem] whitespace-normal text-gray-600" },
];

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

/** Every lesson in the year: one class's or teacher's week at a time, who carries the load, and the full list. */
export function LessonsPanel({ yearName, slots, periods, classes, teachers, isLoading, isError, onRetry }: {
  yearName: string;
  slots: TimetableSlot[] | undefined;
  periods: TimetablePeriod[];
  classes: Option[];
  teachers: Teacher[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}) {
  const [mode, setMode] = useState<"class" | "teacher">("class");
  const [layout, setLayout] = useWeekLayout();
  const [who, setWho] = useState("");
  const [viewing, setViewing] = useState<TimetableSlot | null>(null);
  const [downloadError, setDownloadError] = useState("");
  const periodMap = useMemo(() => new Map(periods.map((period) => [period.id, period])), [periods]);
  const all = useMemo(() => slots ?? [], [slots]);
  const week = who ? all.filter((slot) => (mode === "class" ? slot.class_assigned : slot.teacher) === Number(who)) : [];
  const choices = mode === "class" ? classes.map((item) => ({ id: item.id, label: item.name })) : teachers.map((item) => ({ id: item.id, label: teacherName(item) }));
  const whoLabel = choices.find((item) => String(item.id) === who)?.label ?? "";
  const classLevel = mode === "class" ? classes.find((item) => String(item.id) === who)?.level : undefined;

  const columns: RegisterColumn<TimetableSlot>[] = [
    { header: "Day", cell: (row) => dayName(row.day_of_week), exportValue: (row) => dayName(row.day_of_week), className: "text-gray-700" },
    { header: "Period", cell: (row) => row.period_name, exportValue: (row) => row.period_name, className: "text-gray-700" },
    {
      header: "Time",
      cell: (row) => (periodMap.get(row.period) ? periodTime(periodMap.get(row.period)!) : ""),
      exportValue: (row) => (periodMap.get(row.period) ? periodTime(periodMap.get(row.period)!) : ""),
      className: "whitespace-nowrap tabular-nums text-gray-700",
    },
    { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span>, exportValue: (row) => row.class_name },
    { header: "Subject", cell: (row) => row.subject_name, exportValue: (row) => row.subject_name, className: "min-w-[9rem] whitespace-normal text-gray-700" },
    { header: "Teacher", cell: (row) => row.teacher_name, exportValue: (row) => row.teacher_name, className: "text-gray-700" },
    { header: "Room", cell: (row) => row.room || <span className="text-gray-300">Classroom</span>, exportValue: (row) => row.room || "Classroom", className: "text-gray-700" },
  ];

  const openWeek = (nextMode: "class" | "teacher", id: number) => {
    setMode(nextMode);
    setWho(String(id));
    setViewing(null);
    document.getElementById("week-view")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const downloadPdf = async () => {
    try {
      const blob = mode === "class" ? await timetableService.downloadClassTimetablePdf(Number(who)) : await timetableService.downloadTeacherTimetablePdf(Number(who));
      downloadBlob(blob, `timetable-${whoLabel.replace(/\s+/g, "-") || mode}.pdf`);
      setDownloadError("");
    } catch {
      setDownloadError("The PDF could not be downloaded. Try again in a moment.");
    }
  };

  return (
    <div className="space-y-6">
      <section id="week-view" className="card">
        <div className="card-header pb-4">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">A week at a glance</h3>
              <p className="mt-1 text-sm text-gray-500">One class's or one teacher's week, as they see it. Open a lesson for its details.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <LayoutSwitch layout={layout} onChange={setLayout} />
              <div role="group" aria-label="Whose week" className="segmented">
                {(["class", "teacher"] as const).map((value) => (
                  <button key={value} type="button" aria-pressed={mode === value} onClick={() => { setMode(value); setWho(""); }} className="segmented-option">
                    {value === "class" ? "A class" : "A teacher"}
                  </button>
                ))}
              </div>
              <label className="filter-field min-w-56">
                {mode === "class" ? "Class" : "Teacher"}
                <select value={who} onChange={(event) => setWho(event.target.value)} className="input-compact">
                  <option value="">Choose…</option>
                  {choices.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
              </label>
              <button type="button" disabled={!who} onClick={downloadPdf} className="btn btn-outline h-9 gap-1.5 px-3 text-sm">
                <Download className="h-4 w-4" /> PDF
              </button>
            </div>
          </div>
        </div>
        <div className="card-content space-y-3">
          {downloadError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{downloadError}</p>}
          {!who ? (
            <p className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">Choose a {mode} to see their week.</p>
          ) : week.length ? (
            layout === "periods" ? (
              <PeriodGrid slots={week} periods={periods} onOpen={setViewing} classLevel={classLevel} detail={(slot) => (mode === "class" ? slot.teacher_name : slot.class_name)} />
            ) : (
              <WeekGrid slots={week} periods={periods} onOpen={setViewing} detail={(slot) => (mode === "class" ? slot.teacher_name : slot.class_name)} />
            )
          ) : (
            <p className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">No lessons for {whoLabel} in {yearName} yet.</p>
          )}
        </div>
      </section>

      <ClientRegister
        title="Teaching load"
        description="Lessons a week for each teacher in the timetable now in use."
        rows={teacherLoads(all)}
        config={loadConfig}
        columns={loadColumns}
        rowKey={(row) => row.teacher}
        onOpen={(row) => openWeek("teacher", row.teacher)}
        openLabel={() => "See week"}
        noun={["teacher", "teachers"]}
        exportName={{ file: "teaching-load", title: `Teaching Load ${yearName}` }}
        isLoading={isLoading}
        emptyText="No lessons yet. Generate the timetable to see each teacher's load."
      />

      <ClientRegister
        title={`All lessons · ${yearName}`}
        description="Every lesson in the timetable now in use."
        rows={slots}
        config={lessonConfig(periodMap)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={setViewing}
        noun={["lesson", "lessons"]}
        exportName={{ file: "timetable-lessons", title: `Timetable ${yearName}` }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No lessons yet. Generate the timetable once periods and teaching assignments are set."
      />

      {viewing && (() => {
        const period = periodMap.get(viewing.period);
        return (
          <DetailsModal
            title={`${viewing.subject_name} · ${viewing.class_name}`}
            headline={{ label: dayName(viewing.day_of_week), value: period ? periodTime(period) : viewing.period_name, note: viewing.period_name }}
            facts={[
              ["Class", viewing.class_name],
              ["Subject", viewing.subject_name],
              ["Teacher", viewing.teacher_name],
              ["Room", viewing.room || "Their classroom"],
              ["Day", dayName(viewing.day_of_week)],
              ["Period", viewing.period_name],
            ]}
            onClose={() => setViewing(null)}
            footer={
              <>
                <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
                <button type="button" className="btn btn-outline" onClick={() => openWeek("class", viewing.class_assigned)}>{viewing.class_name}'s week</button>
                <button type="button" className="btn btn-primary" onClick={() => openWeek("teacher", viewing.teacher)}>{viewing.teacher_name}'s week</button>
              </>
            }
          />
        );
      })()}
    </div>
  );
}
